import { useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import api from "../services/api";
import { CurrentUserDto, Permissions, NavigationPermissions } from "../types/django/user";
import { authService } from "../services/authService";

const useCurrentUser = (enabled: boolean = true) => {
  const token = authService.getAccessToken();
  const hasToken = !!token;

  return useQuery<CurrentUserDto>({
    queryKey: ["current-user"],
    queryFn: async () => {
      const res = await api.get("/auth/me/");
      return res.data;
    },
    enabled: hasToken && enabled,
    retry: false,
  });
};

export const usePermission = () => {
  const location = useLocation();

  const pathname = location.pathname.toLowerCase().replace(/\/$/, "") || "/";
  const isPublicRoute = [
    "/login",
    "/signup",
    "/activate-account",
    "/activate",
  ].includes(pathname);

  const { data: user, isLoading, error } = useCurrentUser(!isPublicRoute);

  const token = authService.getAccessToken();
  const hasToken = !!token;

  const isReallyLoading = isPublicRoute
    ? false
    : hasToken
    ? (isLoading || !user) && !error
    : false;

  const hasPermission = (permissionName: keyof Permissions): boolean => {
    if (!user) return false;
    const isAdmin =
      user.role?.toLowerCase() === "admin" ||
      user.roles?.some((r) => r.name?.toLowerCase() === "admin");

    if (isAdmin) return true;

    const permissionValue = user.permissions?.[permissionName];
    return !!permissionValue;
  };

  const hasAnyPermission = (permissionNames: (keyof Permissions)[]): boolean => {
    return permissionNames.some((perm) => hasPermission(perm));
  };
  const hasNavAccess = (navKey: keyof NavigationPermissions): boolean => {
    if (!user) return false;
    const isAdmin =
      user.role?.toLowerCase() === "admin" ||
      user.roles?.some((r) => r.name?.toLowerCase() === "admin");

    if (isAdmin) return true;

    // Forcer le typage explicite pour que TS sache exactement à quoi on accède
    const nav: NavigationPermissions = user.navigation || {
      employees: false,
      human_resources: false,
      planning: false,
      timesheets: false,
      leave_requests: false,
      managed_department_employees: false,
      managed_department_planning: false,
      managed_department_timesheets: false,
      managed_department_leave_requests: false,
    };

    // 1. Si la clé demandée existe explicitement
    if (nav[navKey] !== undefined) {
      return !!nav[navKey];
    }

    // 2. Si l'utilisateur gère des départements
    if (nav.managed_departments) {
      if (
        (typeof navKey === "string" && navKey.startsWith("managed_department_")) ||
        navKey === "employees" ||
        navKey === "timesheets" ||
        navKey === "planning" ||
        navKey === "leave_requests"
      ) {
        return true;
      }
    }

    return false;
  };

  return {
    hasPermission,
    hasAnyPermission,
    hasNavAccess,
    isLoading: isReallyLoading,
    user,
  };
};